import { HTMLAttributes, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import type { PaperProps } from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import PinOutlined from '@mui/icons-material/PinOutlined';
import ShortText from '@mui/icons-material/ShortText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { getUserList } from '../../../../api/users-api';
import useDebounce from '../../../../utils/useDebounce';
import {
  badgeTextColor,
  LABEL_LIMITS,
  LabelFieldForm,
  LabelOptionForm,
} from './label-schema';

/** Figma's multi-select shows two chips, then the rest as "N+". */
const MAX_VISIBLE_CHIPS = 2;

const DATE_DISPLAY_FORMATS = {
  LONG_DATE: 'MMMM D, YYYY',
  SHORT_DATE: 'MM/DD/YYYY',
} as const;

/**
 * The dark theme gives outlined inputs a white border, but date pickers draw their own outline, so
 * this matches the other preview fields. Error and focus keep the picker's own colors.
 */
const DARK_MODE_DATE_OUTLINE: SxProps<Theme> = (theme) =>
  theme.applyStyles('dark', {
    '& .MuiPickersOutlinedInput-root:not(.Mui-error):not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline':
      { borderColor: theme.palette.common.white },
    '& .MuiPickersOutlinedInput-root:not(.Mui-error):not(.Mui-focused):hover .MuiPickersOutlinedInput-notchedOutline':
      { borderColor: theme.palette.text.primary },
  });

interface PreviewFieldProps {
  field: LabelFieldForm;
}

const optionLabel = (option: LabelOptionForm) => option.name || 'Untitled';

function BadgeChip({ option }: { option: LabelOptionForm }) {
  return (
    <Chip
      size="small"
      label={optionLabel(option)}
      sx={{
        bgcolor: option.badgeColor || 'transparent',
        color: badgeTextColor(option.badgeColor),
        maxWidth: '100%',
      }}
    />
  );
}

/** Badge list and single select: pick one option; the x clears it. */
function SingleChoicePreview({ field }: PreviewFieldProps) {
  const [value, setValue] = useState<LabelOptionForm | null>(null);
  const isBadge = field.type === 'BADGE_LIST';
  return (
    <Autocomplete
      size="small"
      options={field.options}
      value={value}
      onChange={(_event, next) => setValue(next)}
      getOptionLabel={optionLabel}
      // Edit Label: a disabled option still shows, but can't be picked.
      getOptionDisabled={(option) => !!option.disabled}
      isOptionEqualToValue={(option, selected) => option.key === selected.key}
      // Like the design: once something is picked, the x that removes it replaces the chevron.
      forcePopupIcon={!value}
      sx={{
        '& .MuiAutocomplete-clearIndicator': {
          visibility: value ? 'visible' : 'hidden',
        },
      }}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          {isBadge ? <BadgeChip option={option} /> : optionLabel(option)}
        </Box>
      )}
      renderValue={
        isBadge ? (selected) => <BadgeChip option={selected} /> : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.name}
          required={field.required}
          error={field.required && !value}
        />
      )}
    />
  );
}

interface SelectAllPaperProps extends PaperProps {
  onSelectAll?: () => void;
  onClear?: () => void;
}

/** The multi-select's option list, with Select all / Clear above the options. */
function SelectAllPaper({
  onSelectAll,
  onClear,
  children,
  ...props
}: SelectAllPaperProps) {
  return (
    <Paper {...props}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 2, pt: 1 }}
        // Keeps the list open while clicking the actions.
        onMouseDown={(event) => event.preventDefault()}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onSelectAll}
        >
          Select all
        </Link>
        <Link
          component="button"
          type="button"
          variant="body2"
          color="text.secondary"
          onClick={onClear}
        >
          Clear
        </Link>
      </Stack>
      {children}
    </Paper>
  );
}

/** Multi-select: checkboxes, Select all / Clear, and at most two chips shown. */
function MultiChoicePreview({ field }: PreviewFieldProps) {
  const [value, setValue] = useState<LabelOptionForm[]>([]);
  const atLimit = value.length >= LABEL_LIMITS.maxMultiSelectValues;

  return (
    <Autocomplete
      size="small"
      multiple
      disableCloseOnSelect
      // Clearing is done with Clear in the list, like the design; no x on the field.
      disableClearable
      options={field.options}
      value={value}
      onChange={(_event, next) => setValue(next)}
      getOptionLabel={optionLabel}
      getOptionDisabled={(option) =>
        !!option.disabled ||
        (atLimit && !value.some((selected) => selected.key === option.key))
      }
      isOptionEqualToValue={(option, selected) => option.key === selected.key}
      slots={{ paper: SelectAllPaper }}
      slotProps={{
        paper: {
          onSelectAll: () =>
            setValue(field.options.slice(0, LABEL_LIMITS.maxMultiSelectValues)),
          onClear: () => setValue([]),
        } as PaperProps,
      }}
      // One line, open or closed: up to two chips (cut off with an ellipsis) and an "N+" chip.
      renderValue={(selected) => (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}
        >
          {selected.slice(0, MAX_VISIBLE_CHIPS).map((option) => (
            <Chip
              key={option.key}
              size="small"
              variant="outlined"
              label={optionLabel(option)}
              sx={{ maxWidth: 110 }}
            />
          ))}
          {selected.length > MAX_VISIBLE_CHIPS && (
            <Chip
              size="small"
              variant="outlined"
              label={`${selected.length - MAX_VISIBLE_CHIPS}+`}
            />
          )}
        </Stack>
      )}
      sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
      renderOption={({ key, ...props }, option, { selected }) => (
        <Box component="li" key={key} {...props}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
          {optionLabel(option)}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.name}
          required={field.required}
          error={field.required && value.length === 0}
        />
      )}
    />
  );
}

function NumberPreview({ field }: PreviewFieldProps) {
  const [value, setValue] = useState('');
  const invalid = value !== '' && !/^-?\d+$/.test(value.trim());
  const missing = field.required && value.trim() === '';
  return (
    <TextField
      size="small"
      fullWidth
      label={field.name}
      required={field.required}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      error={invalid || missing}
      helperText={invalid ? 'Whole numbers only' : undefined}
      slotProps={{
        htmlInput: { inputMode: 'numeric' },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {invalid ? (
                <ErrorIcon
                  color="error"
                  fontSize="small"
                  aria-label="Invalid number"
                />
              ) : (
                <PinOutlined
                  fontSize="small"
                  color={missing ? 'error' : 'action'}
                />
              )}
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

function DatePreview({ field }: PreviewFieldProps) {
  const [value, setValue] = useState<Dayjs | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <DatePicker
      label={field.name}
      value={value}
      onChange={setValue}
      // Like the design, clicking anywhere on the field opens the date picker.
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      format={DATE_DISPLAY_FORMATS[field.dateFormat]}
      // The design uses the outlined calendar.
      slots={{ openPickerIcon: CalendarTodayOutlined }}
      slotProps={{
        // Empty: the calendar icon. Picked: an x that clears it instead.
        field: { clearable: true },
        openPickerButton: value ? { sx: { display: 'none' } } : undefined,
        // MUI X only puts the clear button at the edge when the calendar button is elsewhere; it's
        // hidden once a date is picked, so line the x up with the other fields' x.
        clearButton: { edge: 'end' },
        textField: {
          size: 'small',
          fullWidth: true,
          required: field.required,
          error: field.required && !value,
          onClick: () => setOpen(true),
          sx: DARK_MODE_DATE_OUTLINE,
        },
      }}
    />
  );
}

function TextPreview({ field }: PreviewFieldProps) {
  const [value, setValue] = useState('');
  const missing = field.required && value.trim() === '';
  return (
    <TextField
      size="small"
      fullWidth
      label={field.name}
      required={field.required}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      error={missing}
      helperText={`${value.length}/${LABEL_LIMITS.maxTextLength}`}
      slotProps={{
        htmlInput: { maxLength: LABEL_LIMITS.maxTextLength },
        // Counter on the right, like the design's Text field and the Person counters.
        formHelperText: { sx: { textAlign: 'right' } },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <ShortText
                fontSize="small"
                color={missing ? 'error' : 'action'}
              />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

interface PersonOption {
  email: string;
  name: string;
}

// Same page shape the people search sends (read-only index search).
const personSearchPage = () => ({
  cursor: null,
  results: [],
  nameSpace: '',
  firstPull: true,
  searchTime: 0,
  pageSize: 5,
});

/**
 * People picker as the design shows it: search by name or email, people listed with avatar, name
 * and email, picked people as chips with an avatar, and a "picked/limit" count (one person, or up
 * to ten when the field allows multiple).
 */
function PersonPreview({ field }: PreviewFieldProps) {
  const theme = useTheme();
  const [value, setValue] = useState<PersonOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const search = useDebounce(inputValue, 500);
  const limit = field.multipleSelections ? LABEL_LIMITS.maxPeople : 1;
  const atLimit = value.length >= limit;

  const { data } = useQuery({
    queryKey: ['LabelPreviewPeople', search],
    queryFn: () => getUserList(personSearchPage(), search, '', null, null),
    enabled: search.trim() !== '',
    retry: false,
  });
  const options: PersonOption[] = (data?.results ?? []).map((user) => ({
    email: user.userEmail,
    name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
  }));

  const avatar = (
    <Avatar
      sx={{
        bgcolor: theme.palette.semantic.tags.iconBg,
        color: theme.palette.semantic.tags.icon,
      }}
    >
      <PersonIcon sx={{ fontSize: 14 }} />
    </Avatar>
  );

  return (
    <Autocomplete
      size="small"
      multiple
      filterSelectedOptions
      filterOptions={(found) => found}
      options={options}
      value={value}
      onChange={(_event, next) => setValue(next.slice(0, limit))}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      getOptionLabel={(option) => option.name || option.email}
      isOptionEqualToValue={(option, selected) =>
        option.email === selected.email
      }
      getOptionDisabled={() => atLimit}
      noOptionsText={inputValue ? 'No people found' : 'Type a name or email'}
      // Empty: the person icon; once someone is picked, the x that clears everyone.
      popupIcon={<AccountCircleOutlined />}
      forcePopupIcon={value.length === 0}
      sx={{
        '& .MuiAutocomplete-popupIndicatorOpen': { transform: 'none' },
        '& .MuiAutocomplete-clearIndicator': {
          visibility: value.length > 0 ? 'visible' : 'hidden',
        },
      }}
      renderValue={(selected, getItemProps) =>
        selected.map((person, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              {...itemProps}
              size="small"
              variant="outlined"
              avatar={avatar}
              label={person.name || person.email}
            />
          );
        })
      }
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 32, height: 32 }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.name || option.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.email}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.name}
          required={field.required}
          error={field.required && value.length === 0}
          helperText={
            value.length > 0 ? (
              <Box
                component="span"
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>{atLimit ? 'Limit reached' : ''}</span>
                <span>{`${value.length}/${limit}`}</span>
              </Box>
            ) : undefined
          }
        />
      )}
    />
  );
}

/**
 * A working control for one label field, showing how it behaves when users set it in Drive or
 * Gmail. Values live only in this preview and are never saved. Like the design, a required field
 * shows as an error until it has a value.
 */
export default function PreviewField({
  field,
  ...props
}: PreviewFieldProps & HTMLAttributes<HTMLDivElement>) {
  return (
    <Box {...props}>
      {(field.type === 'BADGE_LIST' || field.type === 'SINGLE_SELECT') && (
        <SingleChoicePreview field={field} />
      )}
      {field.type === 'MULTI_SELECT' && <MultiChoicePreview field={field} />}
      {field.type === 'NUMBER' && <NumberPreview field={field} />}
      {field.type === 'DATE' && <DatePreview field={field} />}
      {field.type === 'TEXT' && <TextPreview field={field} />}
      {field.type === 'PERSON' && <PersonPreview field={field} />}
    </Box>
  );
}
